import { useEffect, useState, type FormEvent } from 'react';
import type { ReviewOrganization } from '@urbanik/api-client';
import { CanonicalCard, TextField } from '@urbanik/ui';
import { reviewApiBase } from './api-base';
type Batch = {
  id: string;
  fingerprint: string;
  status: string;
  total: number;
  imported: number;
  review: number;
  startedAt: string;
  completedAt: string | null;
};
type Input = {
  nationalId: string;
  givenNames: string;
  familyNames: string;
  slotNumber: string;
  reason: string;
};
async function call<T>(organizationId: string, path: string, init?: RequestInit) {
  const response = await fetch(
      `${reviewApiBase}/identity/multi-review/organizations/${organizationId}/imports${path}`,
      {
        ...init,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'x-urbanik-review': '1',
        },
      },
    ),
    body = await response.json().catch(() => null);
  if (!response.ok) throw new Error(body?.message ?? 'No pudimos completar la solicitud.');
  return body as T;
}
export function EntityImportReviewPanel({ organization }: { organization: ReviewOrganization }) {
  const [input, setInput] = useState<Input>({
      nationalId: '9999999999998',
      givenNames: 'PERSONA',
      familyNames: 'IMPORTADA FICTICIA',
      slotNumber: 'TEST-001',
      reason: 'Importación ficticia para validar aislamiento por entidad',
    }),
    [batches, setBatches] = useState<Batch[]>([]),
    [simulation, setSimulation] = useState<{
      fingerprint: string;
      writesPerformed: boolean;
    } | null>(null),
    [error, setError] = useState(''),
    [message, setMessage] = useState(''),
    [busy, setBusy] = useState(false);
  const load = () =>
    void call<{ items: Batch[] }>(organization.id, '')
      .then(({ items }) => setBatches(items))
      .catch((failure) =>
        setError(failure instanceof Error ? failure.message : 'Error inesperado.'),
      );
  useEffect(() => {
    setSimulation(null);
    setMessage('');
    setError('');
    load();
  }, [organization.id]);
  const field = (key: keyof Input, value: string) =>
    setInput((current) => ({ ...current, [key]: value }));
  async function simulate(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const result = await call<{ fingerprint: string; writesPerformed: boolean }>(
        organization.id,
        '/simulate',
        { method: 'POST', body: JSON.stringify(input) },
      );
      setSimulation(result);
      setMessage('Simulación terminada: no realizó escrituras.');
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Error inesperado.');
    } finally {
      setBusy(false);
    }
  }
  async function execute() {
    setBusy(true);
    setError('');
    try {
      const result = await call<{ duplicateBlocked: boolean; batches: Batch[] }>(
        organization.id,
        '/execute',
        { method: 'POST', body: JSON.stringify(input) },
      );
      setBatches(result.batches);
      setMessage(
        result.duplicateBlocked
          ? 'Duplicado bloqueado: se conservó el lote existente.'
          : `Lote ejecutado únicamente en ${organization.code}.`,
      );
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Error inesperado.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section aria-label={'Importaciones de ' + organization.code}>
      <h2>Importaciones por entidad</h2>
      <p>
        Simular nunca escribe. Ejecutar requiere entidad, permiso y motivo; la misma huella puede
        existir en A y B sin mezclar lotes o cupos.
      </p>
      <CanonicalCard className="entity-review-card">
        <h3>Archivo ficticio de una fila</h3>
        <form onSubmit={(event) => void simulate(event)}>
          <TextField
            id={'import-id-' + organization.id}
            label="Identificación ficticia (13 dígitos, inicia en 9)"
            required
            value={input.nationalId}
            onChange={(event) => field('nationalId', event.target.value)}
          />
          <TextField
            id={'import-given-' + organization.id}
            label="Nombres"
            required
            value={input.givenNames}
            onChange={(event) => field('givenNames', event.target.value)}
          />
          <TextField
            id={'import-family-' + organization.id}
            label="Apellidos"
            required
            value={input.familyNames}
            onChange={(event) => field('familyNames', event.target.value)}
          />
          <TextField
            id={'import-slot-' + organization.id}
            label="Cupo ficticio TEST-*"
            required
            value={input.slotNumber}
            onChange={(event) => field('slotNumber', event.target.value.toUpperCase())}
          />
          <TextField
            id={'import-reason-' + organization.id}
            label="Motivo obligatorio (mínimo 10 caracteres)"
            required
            minLength={10}
            maxLength={500}
            value={input.reason}
            onChange={(event) => field('reason', event.target.value)}
          />
          <button className="ui-button ui-button--secondary" disabled={busy}>
            Simular sin escribir
          </button>
          {simulation ? (
            <button
              className="ui-button ui-button--primary"
              type="button"
              disabled={busy || !organization.permissions.includes('members.import.execute')}
              onClick={() => void execute()}
            >
              Confirmar ejecución en {organization.code}
            </button>
          ) : null}
        </form>
        {error ? (
          <p className="entity-review-banner" role="alert">
            {error}
          </p>
        ) : null}
        {message ? (
          <p className="entity-review-banner" role="status" aria-live="polite">
            {message}
          </p>
        ) : null}
        {simulation ? (
          <p>
            Huella: <code>{simulation.fingerprint.slice(0, 16)}…</code> · Escrituras:{' '}
            {simulation.writesPerformed ? 'sí' : 'no'}
          </p>
        ) : null}
      </CanonicalCard>
      <CanonicalCard className="entity-review-card">
        <h3>Lotes exclusivos de {organization.code}</h3>
        {batches.length ? (
          <ul>
            {batches.map((batch) => (
              <li key={batch.id}>
                {batch.status} · {batch.imported}/{batch.total} importado ·{' '}
                {batch.fingerprint.slice(0, 12)}…
              </li>
            ))}
          </ul>
        ) : (
          <p>No existen lotes en esta entidad.</p>
        )}
      </CanonicalCard>
    </section>
  );
}
